import { PropsWithChildren } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import type { Tailwind as TailwindTypes } from '@/tailwind-types'
import { cn } from '@/utils'

export const headingVariants = tv({
  base: 'font-heading leading-default text-surface-black-50',
  variants: {
    italic: {
      true: 'italic',
      false: ''
    },
    level: {
      h1: '',
      h2: '',
      h3: '',
      h4: '',
      h5: '',
      h6: ''
    },
    size: {
      'heading-h1':
        'text-heading-h1 xs:text-heading-h1-xs sm:text-heading-h1-sm md:text-heading-h1-md lg:text-heading-h1-lg xl:text-heading-h1-xl 2xl:text-heading-h1-2xl 3xl:text-heading-h1-3xl 4xl:text-heading-h1-4xl',
      'heading-h2':
        'text-heading-h2 xs:text-heading-h2-xs sm:text-heading-h2-sm md:text-heading-h2-md lg:text-heading-h2-lg xl:text-heading-h2-xl 2xl:text-heading-h2-2xl 3xl:text-heading-h2-3xl 4xl:text-heading-h2-4xl',
      'heading-h3':
        'text-heading-h3 xs:text-heading-h3-xs sm:text-heading-h3-sm md:text-heading-h3-md lg:text-heading-h3-lg xl:text-heading-h3-xl 2xl:text-heading-h3-2xl 3xl:text-heading-h3-3xl 4xl:text-heading-h3-4xl',
      'heading-h4':
        'text-heading-h4 xs:text-heading-h4-xs sm:text-heading-h4-sm md:text-heading-h4-md lg:text-heading-h4-lg xl:text-heading-h4-xl 2xl:text-heading-h4-2xl 3xl:text-heading-h4-3xl 4xl:text-heading-h4-4xl',
      'title-xl':
        'text-title-xl xs:text-title-xl-xs sm:text-title-xl-sm md:text-title-xl-md lg:text-title-xl-lg xl:text-title-xl-xl 2xl:text-title-xl-2xl 3xl:text-title-xl-3xl 4xl:text-title-xl-4xl',
      'title-lg':
        'text-title-lg xs:text-title-lg-xs sm:text-title-lg-sm md:text-title-lg-md lg:text-title-lg-lg xl:text-title-lg-xl 2xl:text-title-lg-2xl 3xl:text-title-lg-3xl 4xl:text-title-lg-4xl'
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold'
    }
  },
  defaultVariants: {
    level: 'h3',
    size: 'heading-h4',
    weight: 'medium'
  }
})

export type THeadingProps = PropsWithChildren &
  VariantProps<typeof headingVariants> & {
    color?: TailwindTypes['color']
    margin?: TailwindTypes['margin']
    padding?: TailwindTypes['padding']
    textTransform?: TailwindTypes['textTransform']
  }

export const Heading = ({
  children,
  color,
  italic,
  level,
  margin,
  padding,
  size,
  textTransform,
  weight
}: THeadingProps): JSX.Element => {
  const Tag =
    (level as keyof JSX.IntrinsicElements) ||
    headingVariants.defaultVariants.level

  return (
    <Tag
      className={cn(
        headingVariants({
          italic,
          level,
          size,
          weight
        }),
        color,
        margin,
        padding,
        textTransform
      )}
    >
      {children}
    </Tag>
  )
}
